import { getOrCreateDeviceId } from "./identity";

export const VISITED_PRODUCTS_KEY = "socialocean_visited_products";

export type VisitedProduct = {
  product_id: number;
  product_handle: string | null;
  count: number;
  duration_ms: number;
  last_visit_duration_ms: number;
  last_visited_at: string;
};

type WindowProduct = { id?: number; handle?: string };

function getProductFromWindow(): { id: number; handle: string | null } | null {
  if (typeof window === "undefined") return null;
  const product = (window as unknown as { meta?: { product?: WindowProduct } })
    ?.meta?.product;
  const id = product?.id;
  if (typeof id !== "number" || !Number.isFinite(id) || id <= 0) return null;
  const handle =
    typeof product?.handle === "string" && product.handle.trim() !== ""
      ? product.handle
      : null;
  return { id, handle };
}

export function readVisitedProducts(): VisitedProduct[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(VISITED_PRODUCTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is VisitedProduct =>
        e != null &&
        typeof e === "object" &&
        typeof (e as VisitedProduct).product_id === "number" &&
        typeof (e as VisitedProduct).count === "number" &&
        typeof (e as VisitedProduct).duration_ms === "number",
    );
  } catch {
    return [];
  }
}

export function writeVisitedProducts(list: VisitedProduct[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VISITED_PRODUCTS_KEY, JSON.stringify(list));
  } catch {
    /* ignore quota / private mode */
  }
}

function upsertEntry(
  list: VisitedProduct[],
  entry: VisitedProduct,
): VisitedProduct[] {
  const next = list.filter((e) => e.product_id !== entry.product_id);
  next.push(entry);
  return next;
}

function isFocused(): boolean {
  if (typeof document === "undefined") return false;
  const visible = document.visibilityState === "visible";
  const focused = typeof document.hasFocus === "function" ? document.hasFocus() : true;
  return visible && focused;
}

export function initProductVisitTracker(): () => void {
  const noop = () => {};
  if (typeof window === "undefined") return noop;

  try {
    const product = getProductFromWindow();
    if (product == null) return noop;
    const { id: productId, handle: productHandle } = product;

    const list = readVisitedProducts();
    const existing = list.find((e) => e.product_id === productId);

    const beforeCount = existing?.count ?? 0;
    const beforeDuration = existing?.duration_ms ?? 0;
    const previousVisitDuration = existing?.last_visit_duration_ms ?? 0;

    const newEntry: VisitedProduct = {
      product_id: productId,
      product_handle: productHandle,
      count: beforeCount + 1,
      duration_ms: beforeDuration,
      last_visit_duration_ms: previousVisitDuration,
      last_visited_at: new Date().toISOString(),
    };
    writeVisitedProducts(upsertEntry(list, newEntry));

    const payload = {
      event_type: "product.page_visited",
      visitorId: getOrCreateDeviceId(),
      productId,
      url: window.location.href,
      duration: beforeDuration,
      properties: {
        product_handle: productHandle,
        visit_count: newEntry.count,
        cumulative_duration_ms: beforeDuration,
        last_visit_duration_ms: previousVisitDuration,
      },
    };
    window.Shopify?.analytics?.publish?.("product.page_visited", payload);

    let dwellMs = 0;
    let activeSince: number | null = isFocused() ? Date.now() : null;
    let flushed = false;

    const pause = () => {
      if (activeSince != null) {
        dwellMs += Date.now() - activeSince;
        activeSince = null;
      }
    };
    const resume = () => {
      if (activeSince == null && isFocused()) {
        activeSince = Date.now();
      }
    };

    const flush = () => {
      if (flushed) return;
      flushed = true;
      pause();
      const current = readVisitedProducts();
      const target = current.find((e) => e.product_id === productId);
      const base: VisitedProduct = target ?? newEntry;
      const updated: VisitedProduct = {
        ...base,
        duration_ms: base.duration_ms + dwellMs,
        last_visit_duration_ms: dwellMs,
      };
      writeVisitedProducts(upsertEntry(current, updated));
    };

    const controller = new AbortController();
    const { signal } = controller;
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState === "visible") resume();
        else pause();
      },
      { signal },
    );
    window.addEventListener("focus", resume, { signal });
    window.addEventListener("blur", pause, { signal });
    window.addEventListener("pagehide", flush, { capture: true, signal });
    return () => controller.abort();
  } catch {
    /* never let tracking break the widget */
    return noop;
  }
}
